/**
 * GroupedProductsApiLoader - Handles Shopify Storefront API queries and renders product tiles
 *
 * This class manages the fetching of products using the Shopify Storefront API
 * and renders them as product tiles in a grid layout.
 */
if (!customElements.get('grouped-products-storefront-api-loader')) {
  class GroupedProductsApiLoader extends HTMLElement {
    constructor() {
      super();
      this.storefrontAccessToken = null;
      this.shopDomain = null;
      this.abortController = null;
      this.colorMappings = null;
      this.debug = false;
    }

    connectedCallback() {
      this.initializeConfig();
      this.loadProducts();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    /**
     * Initialize configuration from meta tags or window object
     */
    initializeConfig() {
      this.initializeApiConfig();
      this.initializeColorMappings();
      this.validateConfiguration();
    }

    /**
     * Initialize API configuration
     */
    initializeApiConfig() {
      this.storefrontAccessToken =
        window.grouped_products_api_loader?.storefront_access_token || null;
      const shopDomain = window.grouped_products_api_loader?.shop_domain;
      this.shopDomain = shopDomain || `${window.location.hostname}` || null;
    }

    /**
     * Validate required configuration
     */
    validateConfiguration() {
      if (!this.storefrontAccessToken || !this.shopDomain) {
        this.debug &&
          console.warn(
            'Storefront API token or shop domain not found. Please add it to your theme settings or meta tags.'
          );
      }
      this.debug && console.log('Storefront API token:', this.storefrontAccessToken);
      this.debug && console.log('Shop domain:', this.shopDomain);
    }

    /**
     * Initialize color mappings from settings
     */
    initializeColorMappings() {
      const colorMappingsString = window.grouped_products_api_loader?.color_mappings || null;

      if (!colorMappingsString) {
        this.logColorMappingsWarning();
        return;
      }

      this.colorMappings = new Map();
      this.parseColorMappings(colorMappingsString);
    }

    /**
     * Log warning for missing color mappings
     */
    logColorMappingsWarning() {
      this.debug &&
        console.warn(
          'No color mappings found. Please add them to your theme settings or meta tags.'
        );
    }

    /**
     * Parse color mappings string into Map
     */
    parseColorMappings(colorMappingsString) {
      const lines = colorMappingsString.split('\n');
      lines.forEach((line) => {
        this.processColorMappingLine(line);
      });
    }

    /**
     * Process individual color mapping line
     */
    processColorMappingLine(line) {
      const trimmedLine = line.trim();
      if (!trimmedLine || !trimmedLine.includes(':')) {
        return;
      }

      const [colorName, hexCode] = trimmedLine.split(':').map((s) => s.trim());
      if (colorName && hexCode) {
        this.colorMappings.set(colorName.toLowerCase(), hexCode);
      }
    }

    /**
     * Get hex code for a color name, with fallback
     */
    getColorHex(colorValue) {
      if (!colorValue) return null;

      // If it's already a hex code, return it
      if (colorValue.startsWith('#')) {
        return colorValue;
      }

      // Try to find it in color mappings
      const mappedColor = this.colorMappings.get(colorValue.toLowerCase());
      if (mappedColor) {
        return mappedColor;
      }

      return null;
    }

    /**
     * Build GraphQL query for products with specific tags
     */
    buildGraphQLQuery() {
      const tagQuery = this.dataset.tagQuery;
      const maxProducts = parseInt(this.dataset.maxProducts) || 250;

      if (!tagQuery || tagQuery.trim() === '') {
        this.debug &&
          console.warn('No tag query provided. Available data attributes:', {
            tagQuery: this.dataset.tagQuery,
            sectionId: this.dataset.sectionId,
            maxProducts: this.dataset.maxProducts,
            excludeProduct: this.dataset.excludeProduct,
          });

        this.hideSkeleton();
        return null;
      }

      // Build the query string - filter by tag and availability true
      const queryString = `query: "tag:'${tagQuery}' AND available_for_sale:true"`;

      // Get metafield configuration from data attributes
      const swatchColorNamespace = this.dataset.swatchColorNamespace || 'custom';
      const swatchColorKey = this.dataset.swatchColorKey || 'color';

      const fullQueryString = `        query GetProductColorVariants {
          products(first: ${maxProducts}, ${queryString}) {
            edges {
              node {
                id
                title
                handle
                tags
                featuredImage {
                  altText
                  url
                }
                options {
                  name
                  values
                }
                colorMetafield: metafield(namespace: "${swatchColorNamespace}", key: "${swatchColorKey}") {
                  value
                }
              }
            }
          }
        }
      `;

      this.debug && console.log('Full query string:', fullQueryString);
      return {
        query: fullQueryString,
        variables: {},
      };
    }

    /**
     * Load products using the Storefront API
     */
    async loadProducts() {
      const queryConfig = this.buildGraphQLQuery();
      if (!queryConfig) {
        this.debug && console.warn('No valid query configuration');
        return;
      }

      try {
        const data = await this.fetchProductData(queryConfig);
        this.renderProducts(data.data.products.edges);
      } catch (error) {
        this.handleLoadError(error);
      } finally {
        this.abortController = null;
      }
    }

    /**
     * Fetch product data from Storefront API
     */
    async fetchProductData(queryConfig) {
      this.abortController = new AbortController();

      const response = await fetch(`https://${this.shopDomain}/api/2025-07/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
        },
        body: JSON.stringify(queryConfig),
        signal: this.abortController.signal,
      });

      this.validateResponse(response);
      const data = await response.json();
      this.validateGraphQLResponse(data);

      this.debug && console.log('Data:', data);
      return data;
    }

    /**
     * Validate HTTP response
     */
    validateResponse(response) {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    /**
     * Validate GraphQL response for errors
     */
    validateGraphQLResponse(data) {
      if (data.errors) {
        this.debug && console.log('GraphQL errors:', data.errors);
        throw new Error('GraphQL query failed');
      }
    }

    /**
     * Handle loading errors
     */
    handleLoadError(error) {
      if (error.name === 'AbortError') {
        return; // Request was aborted, don't show error
      }

      this.debug && console.log('Error loading products:', error);
      this.hideSkeleton();
    }

    /**
     * Check if there are enough products to render based on display mode
     */
    hasEnoughProducts(productEdges) {
      const minProducts = 1;
      return productEdges.length >= minProducts;
    }

    /**
     * Render products in the grid
     */
    renderProducts(productEdges) {
      this.debug && console.log('Rendering products:', productEdges);

      if (!this.hasEnoughProducts(productEdges)) {
        this.debug && console.log('Not enough products to render');
        this.hideSkeleton();
        return;
      }

      const { groupedContainer, isReplacingSkeleton } = this.prepareContainer();
      const displayMode = this.dataset.displayMode || 'tiles';

      if (displayMode === 'tiles') {
        this.addHeadingToContainer(groupedContainer, productEdges);
      }

      this.addProductsToContainer(groupedContainer, productEdges);
      this.finalizeContainer(groupedContainer, isReplacingSkeleton);
    }

    /**
     * Prepare the container for rendering products
     */
    prepareContainer() {
      let groupedContainer = this.querySelector('[data-container]');
      let isReplacingSkeleton = false;

      if (!groupedContainer) {
        const skeleton = this.querySelector('[data-skeleton]');
        isReplacingSkeleton = !!skeleton;

        groupedContainer = document.createElement('div');
        groupedContainer.className = 'grouped-products';
        groupedContainer.setAttribute('data-container', '');
      }

      groupedContainer.innerHTML = '';
      return { groupedContainer, isReplacingSkeleton };
    }

    /**
     * Find current product from product edges
     */
    findCurrentProduct(productEdges, currentProductId) {
      if (!currentProductId || !productEdges) return null;

      const productEdge = productEdges.find(({ node: product }) =>
        product.id.includes(currentProductId)
      );

      return productEdge?.node || null;
    }

    /**
     * Get active grouped product title from current product
     */
    getActiveGroupedProductTitle(productEdges, currentProductId) {
      const fallbackTitle = this.dataset.activeGroupedProductTitle || '';
      const currentProduct = this.findCurrentProduct(productEdges, currentProductId);

      if (!currentProduct) {
        return fallbackTitle;
      }

      const colorOption = this.findColorOption(currentProduct);
      const hasSingleColorValue = colorOption?.values?.length === 1;

      return hasSingleColorValue ? colorOption.values[0] : fallbackTitle;
    }

    /**
     * Add heading to the container if configured
     */
    addHeadingToContainer(container, productEdges) {
      const groupedProductsHeading = this.dataset.groupedProductsHeading;
      const activeGroupedProductTitle = this.getActiveGroupedProductTitle(
        productEdges,
        this.dataset.currentProductId
      );

      if (!groupedProductsHeading && !activeGroupedProductTitle) return;

      const headingParts = [
        groupedProductsHeading &&
          `<span class="grouped-products__heading font-semibold">${groupedProductsHeading}</span>`,
        activeGroupedProductTitle &&
          `<span class="grouped-products__heading">${activeGroupedProductTitle}</span>`,
      ].filter(Boolean);

      const headingWrapper = document.createElement('div');
      headingWrapper.innerHTML = `
        <div class="grouped-products__heading-wrapper mb-1.5 flex flex-row gap-2 text-sm">
          ${headingParts.join('')}
        </div>
      `;

      container.appendChild(headingWrapper.firstElementChild);
    }

    /**
     * Add products to the container based on display mode
     */
    addProductsToContainer(container, productEdges) {
      const listContainer = document.createElement('div');
      const displayMode = this.dataset.displayMode || 'tiles';
      if (displayMode === 'swatches') {
        this.renderSwatches(listContainer, productEdges);
      } else {
        this.renderTiles(listContainer, productEdges);
      }

      container.appendChild(listContainer);
    }

    /**
     * Finalize container rendering
     */
    finalizeContainer(container, isReplacingSkeleton) {
      if (isReplacingSkeleton) {
        this.replaceSkeleton(container);
      } else {
        this.appendChild(container);
      }
    }

    /**
     * Hide skeleton loader
     */
    hideSkeleton() {
      const skeleton = this.querySelector('[data-skeleton]');
      if (skeleton) {
        skeleton.classList.add('grouped-products-skeleton--hidden');
      }
    }

    /**
     * Replace skeleton with actual content
     */
    replaceSkeleton(newContent) {
      const skeleton = this.querySelector('[data-skeleton]');
      if (skeleton) {
        // Use replaceWith to maintain layout stability
        skeleton.replaceWith(newContent);
      } else {
        // Fallback: append to container
        this.appendChild(newContent);
      }
    }

    /**
     * Render swatches in the list container
     */
    renderSwatches(listContainer, productEdges) {
      listContainer.className = 'grouped-products__list flex flex-row flex-wrap gap-1.5';

      // Filter products that can create valid swatches
      const validSwatchProducts = productEdges.filter(({ node: product }) => {
        const colorValue = product.colorMetafield?.value;
        return this.getColorHex(colorValue) !== null;
      });

      // Sort products to prioritize current product first
      const currentProductId = this.dataset.currentProductId;
      const sortedSwatchProducts = this.sortProductsWithCurrentFirst(
        validSwatchProducts,
        currentProductId
      );

      // Render up to 3 swatches
      const maxSwatches = 3;
      const swatchesToRender = sortedSwatchProducts.slice(0, maxSwatches);
      const remainingCount = sortedSwatchProducts.length - maxSwatches;

      swatchesToRender.forEach(({ node: product }) => {
        const swatch = this.createColorSwatch(product);
        if (swatch) {
          listContainer.appendChild(swatch);
        }
      });

      // Add remaining count if there are more than 3 valid swatches
      if (remainingCount > 0) {
        // Get the current active product (first in sorted list)
        const activeProduct = sortedSwatchProducts[0]?.node;
        const remainingElement = this.createRemainingCount(remainingCount, activeProduct);
        listContainer.appendChild(remainingElement);
      }
    }

    /**
     * Render tiles in the list container
     */
    renderTiles(listContainer, productEdges) {
      listContainer.className = 'grouped-products__list -ml-0.5 flex flex-row flex-wrap gap-1';
      productEdges.forEach(({ node: product }) => {
        const tile = this.createProductTile(product);
        if (tile) {
          listContainer.appendChild(tile);
        }
      });
    }

    /**
     * Sort products to put current product first
     */
    sortProductsWithCurrentFirst(productEdges, currentProductId) {
      if (!currentProductId) {
        return productEdges;
      }

      const currentProduct = productEdges.find(({ node: product }) =>
        product.id.includes(currentProductId)
      );

      const otherProducts = productEdges.filter(
        ({ node: product }) => !product.id.includes(currentProductId)
      );

      // Return current product first, followed by others
      return currentProduct ? [currentProduct, ...otherProducts] : productEdges;
    }

    /**
     * Create a single product tile or swatch based on display mode
     */
    createProductTile(product) {
      const displayMode = this.dataset.displayMode || 'tiles';

      if (displayMode === 'swatches') {
        return this.createColorSwatch(product);
      }
      return this.createImageTile(product);
    }

    /**
     * Create a color swatch for a product
     */
    createColorSwatch(product) {
      const currentProductId = this.dataset.currentProductId;
      const isCurrentProduct = currentProductId && product.id.includes(currentProductId);

      // Get color information
      const colorValue = product.colorMetafield?.value;
      const colorHex = this.getColorHex(colorValue);

      // If no valid color, don't render swatch
      if (!colorHex) {
        return null;
      }

      // Build product title - use color name if available
      let productTitle = product.title;
      if (colorValue) {
        productTitle = `${product.title} - ${colorValue}`;
      }

      const container = document.createElement('div');
      const baseClasses =
        'grouped-product h-4 w-4 min-w-4 cursor-pointer rounded-full outline-1 p-0.5';
      const activeClasses = isCurrentProduct
        ? 'outline outline-1 outline-neutral-300'
        : 'outline-transparent hover:outline-neutral-300 transition-all duration-300';
      const swatchClasses =
        'grouped-products__swatch hocus:scale-110 relative block h-full overflow-hidden rounded-full bg-[var(--color-swatch-background)] pointer-events-none';

      container.innerHTML = `
         <a
           href="/products/${product.handle}"
           class="${baseClasses} ${activeClasses}"
           title="${productTitle}"
           aria-label="${productTitle}"
         >
           <span
             class="${swatchClasses}"
             style="--color-swatch-background: ${colorHex};"
           >
           </span>
         </a>
       `;

      const link = container.firstElementChild;

      return link;
    }

    /**
     * Create a remaining count element for swatches
     */
    createRemainingCount(count, activeProduct) {
      // Use the active product's URL if available, otherwise fallback to current page
      let productUrl = '#';
      let productTitle = 'View more colors';

      if (activeProduct) {
        productUrl = `/products/${activeProduct.handle}`;
        // Find color option using helper method
        const colorOption = this.findColorOption(activeProduct);
        const colorValue = colorOption?.values?.[0];
        productTitle = colorValue
          ? `${activeProduct.title} - ${colorValue} (+${count} more colors)`
          : `${activeProduct.title} (+${count} more colors)`;
      }

      const container = document.createElement('div');
      container.innerHTML = `
         <a
           href="${productUrl}"
           class="grouped-products__swatch-count text-xs"
           title="${productTitle}"
           aria-label="View ${count} more colors"
         >
           +${count}
         </a>
       `;

      return container.firstElementChild;
    }

    /**
     * Create a single product tile (original functionality)
     */
    createImageTile(product) {
      const currentProductId = this.dataset.currentProductId;
      const isCurrentProduct = currentProductId && product.id.includes(currentProductId);
      const firstImage = product.featuredImage;

      const container = document.createElement('div');
      container.innerHTML = `
         <a
           href="/products/${product.handle}"
           class="grouped-product hocus:border-black h-20 w-15 min-w-15 cursor-pointer rounded-sm border p-0.5 ${isCurrentProduct ? 'border-black' : 'border-transparent'}"
           title="${product.title}"
           aria-label="${product.title}"
         >
           <span class="grouped-products__image relative block h-full overflow-hidden rounded-sm">
             ${
               firstImage
                 ? `<img
               class="w-full inset-0 h-full block object-cover absolute top-0 left-0 pointer-events-none"
               src="${firstImage.url}"
               alt="${firstImage.altText || product.title}"
               width="46"
               height="66"
               sizes="46px"
               loading="lazy"
             >`
                 : ''
             }
           </span>
         </a>
       `;

      const link = container.firstElementChild;
      if (isCurrentProduct) {
        link.addEventListener('click', (e) => e.preventDefault());
        link.style.cursor = 'default';
      }

      return link;
    }

    /**
     * Find color option from product options using configured color option names
     */
    findColorOption(product) {
      if (!product || !product.options || product.options.length === 0) return;

      const colorOptionNamesStr = this.dataset.colorOptionNames;

      if (!colorOptionNamesStr) return;

      const colorOptionNames = colorOptionNamesStr.split(',').map((name) => name.trim());

      return product.options.find((opt) => colorOptionNames.includes(opt.name));
    }
  }

  // Register the custom element
  customElements.define('grouped-products-storefront-api-loader', GroupedProductsApiLoader);
}
